import { clearEditing, setEditing } from '@store/FormCardReducer'
import { useAppDispatch } from '@store/hooks'
import { ConditionalWrapper } from '@supplement-bacon/react-toolkit/utils'
import {
  SBRM,
  SBRMAction,
  SBRMFormInterface,
} from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import {
  App,
  Badge,
  Button,
  Card,
  Flex,
  Segmented,
  Space,
  Typography,
} from 'antd'
import { ReactNode, useEffect, useRef, useState, useTransition } from 'react'
import { DEFAULT_ERROR } from '../App'
import { langList } from '../factories/Utils'
import { SendTestModal } from './notifications/push-wizard/components/SendTestModal'

const { Title, Text } = Typography

interface CommonProps {
  icon?: ReactNode
  onSubmit?: () => void
  multilingual?: boolean
  entity: Entity
  action: SBRMAction
  sections?: string[]
  metadata?: { key: string; value: any }[]
  description?: string
  footer?: ReactNode
  onSuccess?: () => void
  disabled?: boolean
  saveButtonText?: string
  extra?: ReactNode

  /**
   * @warning EXPERIMENTAL
   *
   * Hide the save button and trigger automatically the save action on each changement
   * Be careful, this is still experimental
   */
  saveOnChange?: boolean
  autoSize?: boolean
}

/** Title and actions rendered inside the Ant Design Card header (title + extra slots). Requires a title. */
interface WithCardHeader {
  withCardHeader: true
  title: string

  noCardWrap?: never
}

/** Title and actions rendered above the card as a standalone header row. */
interface WithoutCardHeader {
  withCardHeader?: false
  title?: string

  noCardWrap?: never
}

/** No card wrapper at all — content rendered bare. withCardHeader is incompatible. */
interface NoCardWrap {
  noCardWrap?: boolean
  title?: string

  withCardHeader?: never
}

type Props = CommonProps & (NoCardWrap | WithCardHeader | WithoutCardHeader)

const FormCard = ({
  title,
  icon,
  onSubmit,
  multilingual = false,
  noCardWrap = false,
  description,
  footer,
  onSuccess,
  disabled = false,
  saveButtonText = 'Save',
  extra,
  saveOnChange,
  autoSize = false,
  withCardHeader = false,
  ...formRenderProps
}: Props) => {
  const dispatch = useAppDispatch()
  const { message } = App.useApp()
  const ref = useRef<SBRMFormInterface>(null)
  const [isEditing, setIsEditing] = useState(false)
  // The save button spins for as long as the transition runs, so there is no
  // flag of our own to set and reset around the request.
  const [isSaving, startSaving] = useTransition()
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const metadataCommunication = formRenderProps.metadata?.find(
    (m) => m.key === MetadataKey.Communication
  )

  const metadataCommunicationContext = formRenderProps.metadata?.find(
    (m) => m.key === MetadataKey.CommunicationContext
  )

  const entityKey = formRenderProps.metadata
    ?.map((m) => `${m.key}:${m.value}`)
    .join('|')

  // Cleanup Redux state on unmount
  useEffect(() => {
    return () => {
      if (entityKey) {
        dispatch(clearEditing(entityKey))
      }
    }
  }, [entityKey])

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    },
    []
  )

  const handlePromise = (promise: Promise<any> | undefined) =>
    promise &&
    promise
      .then((_) => {
        message.success('Modifications saved')
        setIsEditing(false)
        if (entityKey) {
          dispatch(clearEditing(entityKey))
        }

        onSuccess && onSuccess()
      })
      .catch((e) => {
        console.error(e)

        message.error(DEFAULT_ERROR)
      })

  const onSaveClicked = () =>
    startSaving(async () => {
      switch (formRenderProps.action) {
        case SBRMAction.Create:
          await handlePromise(ref?.current?.handleCreate())
          break
        case SBRMAction.Update:
          await handlePromise(ref?.current?.handleUpdate())
          break
        case SBRMAction.Delete:
          await handlePromise(ref?.current?.handleDelete())
          break
        default:
          break
      }
      onSubmit && onSubmit()
    })

  const debouncedSave = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      onSaveClicked()
      debounceTimerRef.current = null
    }, 1000)
  }

  const cardTitle = (icon || title) && (
    <>
      {icon} {title}
      {isEditing && (
        <Badge status="processing" color="blue" style={{ marginLeft: 5 }} />
      )}
    </>
  )

  const saveButton = !saveOnChange && (
    <Button
      disabled={disabled}
      loading={isSaving}
      style={{ border: 0 }}
      type="primary"
      onClick={onSaveClicked}
    >
      {saveButtonText}
    </Button>
  )

  const segmented = multilingual && (
    <Segmented
      defaultValue={langList[0].code}
      options={langList.map((lang) => ({ value: lang.code, label: lang.flag }))}
    />
  )

  const actions = (
    <>
      {metadataCommunication && (
        <SendTestModal
          communicationKey={metadataCommunication.value}
          context={metadataCommunicationContext?.value}
        />
      )}
      {saveButton}
      {extra}
    </>
  )

  // Title/actions go inside the Card's built-in header slots
  const showHeader = withCardHeader && !noCardWrap
  // Title/actions go above the card as a standalone row (fallback when not using card header)
  const showOuterHeader = !showHeader && (title || extra || !saveOnChange)
  // Either outer header or multilingual segmented control needs the row rendered
  const showOuterActions = showOuterHeader || multilingual

  return (
    <Flex
      vertical
      className={autoSize ? '' : 'w-full'}
      gap={8}
      style={disabled ? { opacity: 0.4 } : {}}
    >
      {showHeader ? (
        <Flex align="center" justify="flex-end" gap={8}>
          {segmented}
        </Flex>
      ) : showOuterActions ? (
        <Space className="w-full space-between">
          <Title level={5} className="m-0">
            {cardTitle}
          </Title>
          <Space>
            {segmented}
            {actions}
          </Space>
        </Space>
      ) : null}

      <ConditionalWrapper
        condition={!noCardWrap}
        wrapper={(children) => (
          <Card
            title={showHeader && cardTitle}
            extra={showHeader && <Space>{actions}</Space>}
          >
            <Flex vertical>
              {children}
              {description && (
                <Card styles={{ body: { padding: 10 } }}>
                  <Text type="secondary">{description}</Text>
                </Card>
              )}
              {footer}
            </Flex>
          </Card>
        )}
      >
        <SBRM.Form
          ref={ref}
          {...formRenderProps}
          onDirtyChange={(isDirty) => {
            setIsEditing(isDirty)

            if (entityKey) {
              isDirty
                ? dispatch(setEditing(entityKey))
                : dispatch(clearEditing(entityKey))
            }
          }}
          formProps={{
            onValuesChange: () => {
              if (saveOnChange) {
                debouncedSave()
              }
            },
          }}
        />
      </ConditionalWrapper>
    </Flex>
  )
}

export default FormCard
